import { useState, useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'

import { pluralize } from '@aytm/app/stats/lib/util/helpers'
import { calcUidFilter, PINNED_STATUS_CLAUSE } from '@aytm/app/stats/lib/query/uid-filter'
import {
  can,
  CAPABILITY,
  canFilterResponses,
  tierFromFeatures,
  exportAllowedFromFeatures
} from '@aytm/app/stats/page/data-quality-report/lib/tiers'

// components
import { Button } from 'primereact/button'

import SidebarPopupWarning from '@aytm/app/stats/page/sidebar/components/SidebarPopupWarning'
import DataCentrifugeSection from '@aytm/app/stats/page/sidebar/data-quality/DataCentrifugeSection'
import {
  calcPanelQuarantineCount,
  calcWholeSurveyQuarantineCount
} from '@aytm/app/stats/page/sidebar/data-quality/quarantine-counts'

// styles
import style from '@aytm/app/stats/page/sidebar/data-quality/styles/DataQualityPanel.module.scss'

const INTRO =
  'Data quality is a multistage process built on three pillars: high-quality panelists, thoughtful survey design, and automated protection and data purification tools - all reinforced by human-in-the-loop oversight.'
const GUIDE = 'Data Quality report guides you through the entire process, ensuring you receive insights you can trust.'

const DataQualityPanel = ({ root, model, features, onExport, onModeChange }) => {
  const [failed, setFailed] = useState(false)
  const [processing, setProcessing] = useState(false)
  const gateFeatures = useMemo(() => [features?.data_quality_report].filter(Boolean), [features?.data_quality_report])

  // A guest's report is view-only: the backend withholds export by clearing the feature's export flag,
  // so the panel suppresses the affordance regardless of tier. Authorized viewers carry it true.
  const canExport = exportAllowedFromFeatures(features)
  const dc = model.dataCentrifuge || {}
  // The DQR's open accordion section is published onto the DC controller's listProps
  // (sectionOpen) — the Data Centrifuge controls show only while that section is open.
  const dcSectionOpen = Boolean(dc.sectionOpen)
  const { quarantineUIDs, onFilter, onExportChange } = dc

  // Effective DQR tier gates the Data Centrifuge list (e.g. subvectors are hidden below Payg).
  // Prefer the controller's reconciled wire-first tier so the panel gates on the same value as
  // the report body; fall back to features for the first paint before /dqr commits.
  const tier = dc.tier || tierFromFeatures(features)

  const dcQuarantineCount = calcPanelQuarantineCount(tier, dc)

  // The per-vector re-run is offered only to a viewer who can actually start a run — the tier
  // that grants vector selection is wider than the roles the run feature resolves on for.
  const runEnabled = !features?.stat_data_cleaning_run?.disabled

  const showFilter = 0 < dcQuarantineCount && canFilterResponses(tier, features)
  // The file is whole survey, so its label counts what the file holds.
  const exportCount = calcWholeSurveyQuarantineCount(tier, dc)
  const showExport = 0 < exportCount && can(tier, CAPABILITY.EXPORT_QUARANTINED) && canExport

  // A dispatched release stays quarantined until it settles — a failure puts it back — so both
  // actions would go on naming responses the reader has just let go. They stand down for the round
  // trip, the way the Release control itself does, and keep their figures while they wait.
  // The controller's flag covers the rows a data reset takes away while the release still runs.
  const releaseInFlight = 0 < (dc.restoringUIDs?.length || 0) || true === dc.releasing

  // `root` is the scroll/boundary container the Data Centrifuge popups position against.
  // const [root, setRoot] = useState(null)

  // const setComponentRef = useCallback(node => setRoot(node ?? null), [])

  // Mirrors Controls#onHandleFilter, but scoped to the quarantined set rather than the
  // full flagged set, so the filter applies to exactly the responses the label counts.
  const onHandleFilter = useCallback(() => {
    if (!dcQuarantineCount) return

    // The filter is already scoped to the quarantined uids, so the status clause always applies.
    onFilter('uid.*?', calcUidFilter(quarantineUIDs, PINNED_STATUS_CLAUSE))
  }, [quarantineUIDs, dcQuarantineCount, onFilter])

  // The run polls server-side for as long as it takes, and reports back through one callback:
  // `false` when it starts, `true` — or nothing, on the download hop — once it is over either way.
  // The controller keeps the same two facts, since the export outlives this panel: a reader who
  // comes back finds it still running, or its failure, rather than a button that starts another.
  const onHandleExport = useCallback(() => {
    setFailed(false)
    setProcessing(true)
    onExportChange?.({ exporting: true, failed: false })

    onExport((done = true, error = false) => {
      setProcessing(!done)
      if (error) setFailed(true)
      onExportChange?.({ exporting: !done, failed: error })
    })
  }, [onExport, onExportChange])
  const exportBusy = processing || true === dc.exporting
  const exportFailed = failed || true === dc.exportFailed

  return (
    <>
      {/* While the report is closed to this viewer the shared prompt names the way out, as on the other
          panels: back to the quant report in a special mode, sign-in for a locked guest, the upgrade for
          an account without the report. It says nothing while the report is open. */}
      <SidebarPopupWarning
        features={gateFeatures}
        isSpecial={Boolean(model.isSpecialMode)}
        onModeChange={onModeChange}
      />

      <div data-testid="stat-data-quality-panel" className={style.component}>
        <div data-testid="stat-data-quality-panel-title" className={style['component-title']}>
          Data Quality Report
        </div>

        {!dcSectionOpen ? (
          // Intro copy is replaced by the Data Centrifuge controls once that section opens.
          <>
            <div data-testid="stat-data-quality-panel-intro" className={`${style.row} ${style.note}`}>
              {INTRO}
            </div>
            <div data-testid="stat-data-quality-panel-guide" className={`${style.row} ${style.note}`}>
              {GUIDE}
            </div>
          </>
        ) : null}

        {showFilter || showExport ? (
          // Each action carries its own gate — Filter is admin-only, Export runs the tier range below
          // it and is dropped for a view-only (guest) viewer — so the row renders whenever either
          // survives and is absent entirely when neither does.
          <div className={`${style.row} ${style['row-buttons']} p-fluid`}>
            {showFilter ? (
              <Button
                data-testid="stat-data-quality-panel-filter-quarantined"
                className="p-button-rounded p-button-bright-blue"
                label={`Filter ${dcQuarantineCount} quarantined ${pluralize(dcQuarantineCount, 'response', 'responses')}`}
                disabled={releaseInFlight}
                onClick={releaseInFlight ? null : onHandleFilter}
              />
            ) : null}
            {showExport ? (
              <Button
                data-testid="stat-data-quality-panel-export-quarantined"
                className="p-button-rounded p-button-bright-blue p-button-secondary"
                label={`Export ${exportCount} quarantined ${pluralize(exportCount, 'response', 'responses')}`}
                loading={exportBusy}
                disabled={exportBusy || releaseInFlight}
                onClick={exportBusy || releaseInFlight ? null : onHandleExport}
              />
            ) : null}
          </div>
        ) : null}

        {exportFailed ? (
          <div data-testid="stat-data-quality-panel-export-error" className={`${style.row} ${style['row-error']}`}>
            An error occurred while performing selected export operation. If the problem is reproducible please contact
            Support team.
          </div>
        ) : null}

        <div className={style.row}>
          <a
            data-testid="stat-data-quality-panel-guide-link"
            href="https://aytm.showpad.com/share/mcbhuFrUIzQT513cwcKbj"
            className={style['guide-link']}
            target="_blank"
            rel="noopener noreferrer"
          >
            Read our Data Quality Guide
          </a>
        </div>

        {dcSectionOpen ? <DataCentrifugeSection root={root} tier={tier} runEnabled={runEnabled} model={model} /> : null}
      </div>
    </>
  )
}

DataQualityPanel.propTypes = {
  root: PropTypes.any,
  model: PropTypes.object,
  features: PropTypes.object,
  onExport: PropTypes.func,
  onModeChange: PropTypes.func
}
DataQualityPanel.displayName = 'DataQualityPanel'
export default DataQualityPanel
